import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SiloBag, SiloBagCommitmentEvent } from '../models/silo-bag.model';

/**
 * Lista las silobolsas de una empresa vía el proxy del backend, que resuelve la autenticación
 * con Siloreal y ya excluye las comprometidas. El front nunca llama a Siloreal directamente.
 */
@Injectable({
  providedIn: 'root'
})
export class SiloBagService {

  private readonly url = '/:apiBase/companies/:companyId/siloreal/silobags';
  private readonly historyUrl = this.url + '/:silobagId/history';
  private readonly negotiationUrl = '/:apiBase/companies/:companyId/negotiations/:negotiationId/siloreal/silobags';

  constructor(
    private http: HttpClient
  ) { }

  /**
   * @param companyId            Empresa dueña de las silobolsas.
   * @param excludeNegotiationId En edición de contraorden, deja visibles las bolsas ya
   *                             elegidas en esa misma negociación (no las auto-oculta).
   * @param excludeBarterId      En edición de un canje, ídem pero un nivel más arriba: un
   *                             canje son N órdenes y N negociaciones (una por corredor)
   *                             disputando el MISMO grano, así que excluir sólo la
   *                             negociación actual dejaría ocultas las bolsas que el propio
   *                             productor eligió en las hermanas.
   * @param productId            Producto de la orden: sólo se listan las bolsas de ese cultivo.
   */
  public listByCompany(companyId: number, excludeNegotiationId?: number, excludeBarterId?: number | string, productId?: number): Observable<SiloBag[]> {
    const url = this.url.replace(':companyId', companyId.toString());

    let params = new HttpParams();
    if (excludeNegotiationId) {
      params = params.set('exclude_negotiation_id', excludeNegotiationId.toString());
    }
    if (excludeBarterId) {
      params = params.set('exclude_barter_id', excludeBarterId.toString());
    }
    if (productId) {
      params = params.set('product_id', productId.toString());
    }

    return this.http.get<{ data?: any[] }>(url, { params }).pipe(
      map(response => (response?.data ?? []).map(item => SiloBag.fromApi(item)))
    );
  }

  /**
   * Stock del PRODUCTOR de esta negociación, para que la contraparte elija sin tener
   * credenciales propias de Siloreal. El backend ya resuelve exclusión/autorización y, si no
   * se manda `productId`, toma el cultivo de la orden de la negociación.
   */
  public listForNegotiation(companyId: number, negotiationId: number, productId?: number): Observable<SiloBag[]> {
    const url = this.negotiationUrl
      .replace(':companyId', companyId.toString())
      .replace(':negotiationId', negotiationId.toString());

    let params = new HttpParams();
    if (productId) {
      params = params.set('product_id', productId.toString());
    }

    return this.http.get<{ data?: any[] }>(url, { params }).pipe(
      map(response => (response?.data ?? []).map(item => SiloBag.fromApi(item)))
    );
  }

  /**
   * Estado de seguro (`insured`) de todas las silobolsas de la empresa, por SiloID.
   * Se usa para la tabla readonly (snapshot persistido), que no trae ese campo: el seguro
   * puede contratarse recién después de comprometida la bolsa, así que se pide en vivo.
   *
   * @param companyId Empresa dueña de las silobolsas.
   */
  public listInsuredStatus(companyId: number): Observable<Map<string, boolean>> {
    const url = this.url.replace(':companyId', companyId.toString());
    const params = new HttpParams().set('include_committed', '1');

    return this.http.get<{ data?: any[] }>(url, { params }).pipe(
      map(response => {
        const status = new Map<string, boolean>();
        (response?.data ?? []).forEach(item => {
          status.set(String(item.silobag_id ?? ''), item.insured === true);
        });
        // TODO(temporal, quitar): forzar "Bolsa 18" y "Bolsa 17" como aseguradas para poder ver
        // el badge verde mientras Siloreal no tiene ninguna bolsa real con el tag INSURANCE_CREATED.
        // El silo_id (SILOID en la tabla, ej. "FL169NN") no es "18": ese es silobag_name.
        const bolsa18 = (response?.data ?? []).find(item => item.silobag_name === 'Bolsa 18');
        if (bolsa18) {
          status.set(String(bolsa18.silobag_id ?? ''), true);
        }
        const bolsa17 = (response?.data ?? []).find(item => item.silobag_name === 'Bolsa 17');
        if (bolsa17) {
          status.set(String(bolsa17.silobag_id ?? ''), true);
        }
        return status;
      })
    );
  }

  /**
   * Historial de compromiso/liberación de una silobolsa
   * (`GET /:apiBase/companies/:companyId/siloreal/silobags/:silobagId/history`).
   *
   * Se pide bajo demanda (al expandir la fila), no junto con el listado.
   *
   * @param companyId Empresa desde la que se consulta (resuelve permisos en el backend).
   * @param silobagId SiloID de la bolsa cuya trazabilidad se pide.
   */
  public getHistory(companyId: number, silobagId: string): Observable<SiloBagCommitmentEvent[]> {
    const url = this.historyUrl
      .replace(':companyId', companyId.toString())
      .replace(':silobagId', encodeURIComponent(silobagId));

    // El endpoint devuelve un ARRAY PLANO (no viene envuelto en `data`), igual que el
    // historial de cupos que le sirvió de molde. Se tolera igual la forma envuelta por si
    // alguna capa intermedia la agrega.
    return this.http.get<any>(url).pipe(
      map(response => {
        const items = Array.isArray(response) ? response : (response?.data ?? []);
        return items.map(item => this.mapToEvent(item));
      })
    );
  }

  /** Normaliza un evento del backend, tolerando campos ausentes. */
  private mapToEvent(item: any): SiloBagCommitmentEvent {
    return {
      date:                  item?.date ?? null,
      action:                item?.action ?? null,
      reason:                item?.reason ?? null,
      source:                item?.source ?? null,
      // El backend manda `user` y `company` como OBJETOS ({id,name,last_name,...} y
      // {id,name}); acá se aplanan al nombre mostrable. Un usuario del sistema (cron,
      // backfill) llega con `is_system: true` y se deja en null para que el componente
      // muestre la etiqueta traducida "Sistema" en vez de un nombre hardcodeado del backend.
      user:                  this.actorName(item?.user),
      company:               this.displayName(item?.company),
      order_id:              this.toFiniteNumber(item?.order_id ?? null),
      order_reference:       item?.order_reference ?? null,
      negotiation_id:        this.toFiniteNumber(item?.negotiation_id ?? null),
      negotiation_reference: item?.negotiation_reference ?? null,
      barter_proposal_id:    this.toFiniteNumber(item?.barter_proposal_id ?? null),
      details:               item?.details ?? null,
    };
  }

  /**
   * Nombre mostrable del actor de un evento. Devuelve null para actores no humanos
   * (`is_system`), para que el componente use la etiqueta traducida.
   */
  private actorName(user: any): string | null {
    if (!user || user.is_system) {
      return null;
    }

    const full = [user.name, user.last_name].filter(part => !!part).join(' ').trim();

    return full || user.email || null;
  }

  /** Nombre mostrable de una empresa (`{id, name}`), tolerando que ya venga como string. */
  private displayName(company: any): string | null {
    if (!company) {
      return null;
    }

    return typeof company === 'string' ? company : (company.name ?? null);
  }

  private toFiniteNumber(raw: any): number | null {
    if (raw == null || raw === '') return null;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
